import { ProductOptionRecord } from "common/records/ProductOption";
import { ProductOptionGroupRecord } from "common/records/ProductOptionGroup";
import { ProductRecord } from "common/records/Product";
import { RootState } from "common/reducers";
import { Nullable } from "common/types";
import { image } from "common/selectors/image.selectors";
import { recordChanged } from "common/helpers/Record";
import { skuRecord, skuRecordByLastSavedId } from "src/common/selectors/sku.selectors";
import { productOptionsByIds } from "common/selectors/product.selectors";
import { productOptionGroupById } from "common/selectors/productOptionGroup.selectors";
import { productToOptionGroupLink } from "common/selectors/productToOptionGroupLink.selectors";

const model = "productOptions";

/** A template option resolved to the ids the apply path stamps onto a line-item option. */
export interface ResolvedTemplateOption {
  productOptionId: number;
  productOptionGroupId: number;
}

/**
 * Resolves a product's stored template option UUIDs to their ids within the estimate's hydrated
 * snapshot. Walks the product's ptog links → snapshot option group → options, matching by uuid, and
 * returns the snapshot group id (the link's `option_group_id`) — the same group
 * `ADD_ESTIMATE_LINE_ITEM_OPTION` prefers. Results follow the template's `optionUuids` order;
 * options absent from the snapshot are dropped. Mirrors rsf-app `cachedProductOptionsByUuid`.
 *
 * @param {RootState} state The RootState
 * @param {{ product: ProductRecord; optionUuids: string[] }} props
 * @returns {ResolvedTemplateOption[]} the resolved option ids, in template-declared order
 */
export const productOptionsByUuid = (
  state: RootState,
  props: { product: ProductRecord; optionUuids: string[] },
): ResolvedTemplateOption[] => {
  const { product, optionUuids } = props;
  if (!optionUuids || optionUuids.length === 0) return [];

  const byUuid = new Map<string, ResolvedTemplateOption>();
  product.ptog_link_ids.forEach((linkId) => {
    const link = productToOptionGroupLink(state, linkId);
    if (!link) return;
    const group = productOptionGroupById(state, link.option_group_id);
    if (!group) return;
    productOptionsByIds(state, { productOptionIds: group.option_ids }).forEach((option) => {
      if (option.uuid && !byUuid.has(option.uuid)) {
        byUuid.set(option.uuid, { productOptionId: option.id, productOptionGroupId: group.id });
      }
    });
  });

  const resolved: ResolvedTemplateOption[] = [];
  optionUuids.forEach((uuid) => {
    const entry = byUuid.get(uuid);
    if (entry) resolved.push(entry);
  });
  return resolved;
};

/**
 * Resolves a product's option by group + option `match_id` within the estimate's hydrated snapshot.
 * Finds the option group in the product's `option_group_ids` whose `match_id` matches, then the
 * option in that group whose `match_id` matches. `match_id` is the cross-product-stable identifier
 * (an option's `uuid` differs per product), so this is the resolver the estimate-defaults Set/Use
 * flow needs to carry an option from one product's line item to another's. Mirrors rsf-app
 * `cachedProductOptionByMatchId`.
 *
 * @param {RootState} state The RootState
 * @param {{ product: ProductRecord; productOptionGroupMatchId: string; productOptionMatchId: string }} props
 * @returns {Nullable<ProductOptionRecord>} the matching option, or `undefined` if absent from the snapshot
 */
export const productOptionByMatchId = (
  state: RootState,
  props: { product: ProductRecord; productOptionGroupMatchId: string; productOptionMatchId: string },
): Nullable<ProductOptionRecord> => {
  const { product, productOptionGroupMatchId, productOptionMatchId } = props;
  // Walk `option_group_ids` directly (not `ptog_link_ids` like the uuid-keyed `productOptionsByUuid`):
  // this mirrors rsf-app `cachedProductOptionByMatchId` (which resolves against `prod.option_group_ids`)
  // and the option-tab selector `defaultProductOptionGroupsForProduct` — for a hydrated snapshot both
  // routes land on the same fanned groups. `productOptionsByUuid` takes the link route only because it
  // needs the link's `option_group_id` to hand back to `ADD_ESTIMATE_LINE_ITEM_OPTION`; here the caller
  // re-resolves the group from the matched option, so the group id is not needed at this step.
  const group = product.option_group_ids
    .map((id) => productOptionGroupById(state, id))
    .find((g): g is ProductOptionGroupRecord => !!g && g.match_id === productOptionGroupMatchId);
  if (!group) return undefined;
  return productOptionsByIds(state, { productOptionIds: group.option_ids }).find(
    (opt) => opt.match_id === productOptionMatchId,
  );
};

/**
 * Loads the byId map
 *
 * @param {RootState} state The RootState
 * @returns {Map<number, ProductOptionRecord>} The byId map
 */
export const byId = (state: RootState) => state.getIn([model, "byId"]);

/**
 * Loads the lastSavedById map
 *
 * @param {RootState} state The RootState
 * @returns {Map<number, ProductOptionRecord>} The lastSavedById map
 */
export const lastSavedById = (state: RootState) => state.getIn([model, "lastSavedById"]);

/**
 * Loads the product option by the given id
 *
 * @param {RootState} state The RootState
 * @param {number} id
 * @returns {ProductOptionRecord} ProductOptionRecord
 */
export const productOptionById = (state: RootState, id: number): Nullable<ProductOptionRecord> => byId(state).get(id);

/**
 * Loads the product option last saved by the given id
 *
 * @param {RootState} state The RootState
 * @param {number} id
 * @returns {ProductOptionRecord} ProductOptionRecord
 */
export const productOptionLastSavedById = (state: RootState, id: number): Nullable<ProductOptionRecord> =>
  lastSavedById(state).get(id);

/**
 * Checks if the product option have changed on the given product option id
 *
 * @param {RootState} state The RootState
 * @param {number} productId
 * @returns {boolean} True if the product option has changed, false otherwise
 */
export const productOptionChanged = (state: RootState, productId: number): boolean => {
  let dirty = false;
  const productOption = productOptionById(state, productId);
  const productOptionLastSaved = productOptionLastSavedById(state, productId);
  if (productOption && productOptionLastSaved) {
    dirty = recordChanged(productOption, productOptionLastSaved);
  }
  if (productOption?.get("sku_id") && !dirty) {
    const skuId = productOption?.get("sku_id");
    const sku = skuRecord(state, skuId);
    const lastSavedSku = skuRecordByLastSavedId(state, skuId);
    dirty = recordChanged(sku, lastSavedSku);
  }
  return dirty;
};

/**
 * Loads the provided option logo image record
 * Finds the first image of the product option
 *
 * @param {RootState} state The RootState
 * @param {ProductOptionRecord} option
 * @returns {ImageRecord} ImageRecord
 */
export const optionLogo = (state: RootState, option: Nullable<ProductOptionRecord>) => {
  if (!option) {
    return null;
  }

  const imageId = option.image_ids.first();
  if (!imageId) {
    return null;
  }
  return image(state, { imageId });
};
